import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap, take } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import {
  AppleProfilePresetType,
  AppleProfilePresetViewActions,
  PresetModelType,
  selectPresetById,
  selectProfileTypeFromRoute,
} from '@era/stores';
import { Store } from '@ngrx/store';
import { PaneComponent } from '@rocksoft/cdk';
import { RestrictionsSettingsComponent } from '../../../apple-profiles/profile-components/restrictions-profile/restrictions-settings/restrictions-settings.component';
import { WifiProfileComponent } from '../../../apple-profiles/profile-components/wifi-profile/wifi-profile.component';
import { AirplayComponent } from '../../airplay-component/airplay.component';
import { isValidProfileType } from '../profile-preset-type-guard';
import { AuthorizationService, Permissions } from '@rocksoft/shared/authorization';

@Component({
  selector: 'ml-mdm-settings-profile-preset-details',
  templateUrl: './settings-profile-preset-details.container.html',
  imports: [PaneComponent, AirplayComponent, RestrictionsSettingsComponent, WifiProfileComponent, AsyncPipe],
})
export class SettingsProfilePresetDetailsContainerComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly authorizationService = inject(AuthorizationService);

  profileType: AppleProfilePresetType;

  preset: Observable<PresetModelType<AppleProfilePresetType> | undefined>;
  allowWifiEditing$: Observable<boolean>;

  ngOnInit() {
    this.store
      .select(selectProfileTypeFromRoute)
      .pipe(take(1))
      .subscribe((profileType) => {
        if (!profileType || !isValidProfileType(profileType)) {
          throw new Error('profileType is required in route data and must be a valid AppleProfilePresetType');
        }
        this.profileType = profileType;
      });

    this.preset = this.route.params.pipe(
      map((p: Params) => p.id),
      filter((id) => id),
      switchMap((id) => this.store.select(selectPresetById(this.profileType, id))),
      distinctUntilChanged()
    );

    this.allowWifiEditing$ = this.authorizationService.getIsAuthorized$(Permissions.Admin);
  }

  updatePreset(preset: PresetModelType<AppleProfilePresetType>) {
    this.preset.pipe(first()).subscribe((original) => {
      if (!original) {
        return;
      }
      const updatedPreset = this.preparePresetForUpdate(preset, original);

      this.store.dispatch(
        AppleProfilePresetViewActions.presetUpdated({
          profileType: this.profileType,
          originalPreset: original,
          preset: updatedPreset,
        })
      );
    });
  }

  private preparePresetForUpdate(
    preset: PresetModelType<AppleProfilePresetType>,
    original: PresetModelType<AppleProfilePresetType>
  ): PresetModelType<AppleProfilePresetType> {
    const updated = { ...preset, id: original.id };

    return {
      ...updated,
      uniqueId: original.uniqueId,
      payloadDescription: 'payloadDescription' in original ? original.payloadDescription : undefined,
      payloadDisplayName: 'payloadDisplayName' in original ? original.payloadDisplayName : undefined,
      payloadIdentifier: original.payloadIdentifier,
      payloadOrganization: 'payloadOrganization' in original ? original.payloadOrganization : undefined,
      payloadType: original.payloadType,
      payloadUUID: 'payloadUUID' in original ? original.payloadUUID : undefined,
      payloadVersion: original.payloadVersion,
    };
  }
}
